import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Card, CardSm, Input, Label, Select, Pill } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { Plus, MapPin } from '@phosphor-icons/react';
import { healthClassToColor } from '@/lib/utils';

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [trees, setTrees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    plot_code: '', stratum: 'intertidal', area_m2: 100, shape: 'circle',
    centre_lat: '', centre_lon: '',
  });

  const load = async () => {
    const { data } = await api.get(`/projects/${projectId}`);
    setProject(data);
    const t = await api.get('/trees', { params: { project_id: projectId } });
    setTrees(t.data);
  };
  useEffect(() => { load(); }, [projectId]);

  const useGPS = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setForm((f) => ({ ...f, centre_lat: pos.coords.latitude.toFixed(6),
                              centre_lon: pos.coords.longitude.toFixed(6) }));
    });
  };

  const createPlot = async (e) => {
    e.preventDefault();
    await api.post('/plots', {
      project_id: projectId,
      ...form,
      area_m2: Number(form.area_m2),
      centre_lat: Number(form.centre_lat),
      centre_lon: Number(form.centre_lon),
    });
    setShowForm(false);
    setForm({ plot_code: '', stratum: 'intertidal', area_m2: 100, shape: 'circle',
              centre_lat: '', centre_lon: '' });
    load();
  };

  if (!project) return <div className="p-4">Loading…</div>;

  return (
    <div data-testid="project-detail">
      <TopBar title={project.project_name} back />
      <div className="p-4 space-y-3">
        <CardSm>
          <p className="text-xs mono text-carbon-soft uppercase tracking-widest">Methodology · Country</p>
          <p className="font-display font-bold text-base">{project.methodology} · {project.country}</p>
        </CardSm>

        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold">Sample Plots (PSP)</h2>
          <Button size="sm" onClick={() => setShowForm(!showForm)} variant="dark" data-testid="new-plot-btn">
            <Plus size={16} weight="bold" /> Plot
          </Button>
        </div>

        {showForm && (
          <Card data-testid="plot-form">
            <form onSubmit={createPlot} className="space-y-3">
              <div>
                <Label>Plot code</Label>
                <Input value={form.plot_code} onChange={(e) => setForm({ ...form, plot_code: e.target.value })} required data-testid="plot-code-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Stratum</Label>
                  <Input value={form.stratum} onChange={(e) => setForm({ ...form, stratum: e.target.value })} data-testid="plot-stratum-input" />
                </div>
                <div>
                  <Label>Area (m²)</Label>
                  <Input type="number" value={form.area_m2} onChange={(e) => setForm({ ...form, area_m2: e.target.value })} data-testid="plot-area-input" />
                </div>
              </div>
              <div>
                <Label>Shape</Label>
                <Select value={form.shape} onChange={(e) => setForm({ ...form, shape: e.target.value })} data-testid="plot-shape-input">
                  <option value="circle">circle</option>
                  <option value="rectangle">rectangle</option>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Centre lat</Label>
                  <Input type="number" step="any" value={form.centre_lat} onChange={(e) => setForm({ ...form, centre_lat: e.target.value })} required data-testid="plot-lat-input" />
                </div>
                <div>
                  <Label>Centre lon</Label>
                  <Input type="number" step="any" value={form.centre_lon} onChange={(e) => setForm({ ...form, centre_lon: e.target.value })} required data-testid="plot-lon-input" />
                </div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={useGPS} data-testid="plot-use-gps">
                <MapPin size={16} weight="bold" /> Use my GPS
              </Button>
              <Button type="submit" className="w-full" data-testid="create-plot-submit">Create plot</Button>
            </form>
          </Card>
        )}

        <ul className="space-y-2" data-testid="plot-list">
          {(project.plots || []).map((p) => {
            const plotTrees = trees.filter((t) => t.plot_id === p.id);
            return (
              <li key={p.id} className="card-hard-sm p-3" data-testid={`plot-${p.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-display font-bold">{p.plot_code}</span>
                  <Pill color="#2D6A4F">{p.stratum}</Pill>
                </div>
                <div className="text-xs mono text-carbon-soft mb-2">
                  {p.shape} · {p.area_m2} m² · {p.centre_lat.toFixed(4)}, {p.centre_lon.toFixed(4)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs mono">{plotTrees.length} trees</span>
                  <Link to={`/trees/new?plot=${p.id}`} className="text-xs font-display font-bold uppercase tracking-widest underline" data-testid={`register-tree-${p.id}`}>
                    Register tree →
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
